import React from 'react';
import { useNavigate } from 'react-router-dom';
import MetricCard from './MetricCard';

export const DoctorCommandCenter = ({ stats, loading, activeCentreName }) => {
  const navigate = useNavigate();

  const approv = stats?.approval || {};
  const patho = stats?.pathology || {};

  return (
    <div className="lis-command-deck doctor-deck">
      {/* 1. Clinical Review & Approval KPIs */}
      <div className="lis-deck-section">
        <div className="lis-section-header-compact">
          <div className="lis-section-header-left">
            <span className="lis-section-kicker">CLINICAL APPROVAL</span>
            <h3 className="lis-section-title">Diagnostic Review & Validation</h3>
            <span className="lis-section-sub">&bull; {activeCentreName}</span>
          </div>
          <div className="lis-deck-actions">
            <button
              type="button"
              className="lis-deck-action-primary"
              onClick={() => navigate('/reports/approval-queue')}
            >
              Approval Queue
            </button>
            <button
              type="button"
              className="lis-deck-quick-btn"
              onClick={() => navigate('/masters/doctor-signatures')}
            >
              Digital Signature
            </button>
          </div>
        </div>

        <div className="lis-kpi-grid">
          <MetricCard
            title="Reports Awaiting Review"
            value={approv.totalPending ?? 0}
            subtitle="Final clinical validation queue"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/reports/approval-queue"
            variant={approv.totalPending > 0 ? 'warning' : 'success'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            }
          />

          <MetricCard
            title="Critical Panic Alerts"
            value={(approv.totalCritical || 0) + (patho.criticalTodayCount || 0)}
            subtitle="Require immediate physician notification"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/reports/approval-queue"
            routeParams={{ status: 'Critical' }}
            variant={(approv.totalCritical || 0) + (patho.criticalTodayCount || 0) > 0 ? 'danger' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            }
          />

          <MetricCard
            title="Reports on Clinical Hold"
            value={approv.totalHeld ?? 0}
            subtitle="Requires clinician re-consult"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/reports/approval-queue"
            routeParams={{ status: 'Held' }}
            variant={approv.totalHeld > 0 ? 'warning' : 'default'}
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="10" y1="15" x2="10" y2="9" />
                <line x1="14" y1="15" x2="14" y2="9" />
              </svg>
            }
          />

          <MetricCard
            title="Approved & Released Today"
            value={approv.totalApprovedToday ?? 0}
            subtitle="Digitally signed reports"
            status={loading ? 'LOADING' : 'LIVE'}
            route="/reports/approval-queue"
            variant="success"
            icon={
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            }
          />
        </div>
      </div>

      {/* 2. Doctor Reporting Workflow Stages */}
      <div className="lis-deck-section">
        <h4 className="lis-sub-section-title">Clinical Governance & Sign-off Lifecycle</h4>
        <div className="lis-work-tasks-grid">
          <div className="lis-task-card" onClick={() => navigate('/reports/approval-queue')}>
            <div className="lis-task-badge warning">
              {approv.totalPending ?? 0} PENDING VERIFICATION
            </div>
            <h5 className="lis-task-title">Diagnostic Verification & Clinical Review</h5>
            <p className="lis-task-desc">
              Compare observation values with biological reference ranges, inspect delta check graphs, and confirm clinical notes.
            </p>
            <span className="lis-task-link">Open Review Queue &rarr;</span>
          </div>

          <div className="lis-task-card" onClick={() => navigate('/masters/doctor-signatures')}>
            <div className="lis-task-badge success">AUTHENTICATED</div>
            <h5 className="lis-task-title">Cryptographic Digital Signature</h5>
            <p className="lis-task-desc">
              Verify your active digital signature certificate, medical registration number, and clinical stamp for NABL reports.
            </p>
            <span className="lis-task-link">Inspect Signature Master &rarr;</span>
          </div>

          <div className="lis-task-card" onClick={() => navigate('/pathology-results')}>
            <div className="lis-task-badge info">
              {patho.draftCount ?? 0} DRAFTS PENDING
            </div>
            <h5 className="lis-task-title">Pathology Result Workspace</h5>
            <p className="lis-task-desc">
              Examine laboratory bench worklists or revise test values requiring clinical re-evaluation.
            </p>
            <span className="lis-task-link">Open Pathology Results &rarr;</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorCommandCenter;
